import { EInvoiceAddressSchemeType, EInvoiceScheme } from '@components'

import isEmpty from 'lodash/isEmpty'
import { useCallback } from 'react'

import { DEFAULT_CURRENCY } from '@features/invoices/constants/defaultCurrency'
import { DEFAULT_INVOICE_LANGUAGE } from '@features/invoices/constants/defaultInvoiceLanguage'
import { invoiceLanguageToLocale } from '@features/invoices/constants/invoiceLanguageToLocale'
import { selectableInvoiceLanguages } from '@features/invoices/constants/selectableInvoiceLanguages'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { InvoiceFormReceiver } from '@features/invoices/types/invoiceForm/invoiceFormReceiver'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'

import { NumberValue } from '../../../../../utils'
import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'
import { useUpdateDependentDates } from './useUpdateDependentDates'

export const useUpdateReceiverDependentValues = () => {
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { getDefaultSettingsValue } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)
  const { handlePaymentTermChange } = useUpdateDependentDates()

  const { getValues, setValue } = invoiceEditorForm

  const handleLanguageUpdate = useCallback(
    (receiverInvoicingLanguage: string | undefined) => {
      const existingLanguage = getValues('language')
      const defaultSettingsLanguage = existingLanguage || getDefaultSettingsValue('language')
      const invoicingLanguage = receiverInvoicingLanguage || defaultSettingsLanguage
      const invoicingLocale = invoicingLanguage ? invoiceLanguageToLocale[invoicingLanguage] : undefined

      if (invoicingLocale) {
        const defaultLanguage = selectableInvoiceLanguages.includes(invoicingLocale)
          ? invoicingLocale
          : invoiceLanguageToLocale[DEFAULT_INVOICE_LANGUAGE]

        setValue('language', defaultLanguage)
      }
    },
    [getDefaultSettingsValue, getValues, setValue],
  )

  const handleCurrencyUpdate = useCallback(
    (receiverCurrency: string | undefined) => {
      const existingCurrency = getValues('currency')
      const defaultSettingsCurrency = existingCurrency || getDefaultSettingsValue('currency')
      const defaultCurrency = receiverCurrency || defaultSettingsCurrency
      const invoicingCurrency = defaultCurrency || DEFAULT_CURRENCY

      setValue('currency', invoicingCurrency)
    },
    [getDefaultSettingsValue, getValues, setValue],
  )

  const handlePaymentTermUpdate = useCallback(
    (receiverPaymentTerms: number | undefined) => {
      const defaultSettingsPaymentTerms = new NumberValue(getDefaultSettingsValue('paymentTime')).getValue()
      const invoicingPaymentTerms =
        typeof receiverPaymentTerms === 'number' ? receiverPaymentTerms : defaultSettingsPaymentTerms

      setValue('paymentTerm', invoicingPaymentTerms)
      handlePaymentTermChange(invoicingPaymentTerms)
    },
    [getDefaultSettingsValue, handlePaymentTermChange, setValue],
  )

  const handleBuyersReferenceUpdate = useCallback(
    (
      eInvoiceAddressSchemeType: EInvoiceScheme | undefined,
      eInvoiceAddress: string | undefined,
      publicEntityRoutingEnabled: boolean | undefined,
    ) => {
      const shouldUpdateBuyerReference =
        publicEntityRoutingEnabled && eInvoiceAddressSchemeType === EInvoiceAddressSchemeType.LWID
      const updatedBuyerReference = shouldUpdateBuyerReference ? eInvoiceAddress : undefined

      setValue('buyerReference', updatedBuyerReference)
    },
    [setValue],
  )

  const handleReceiverUpdate = useCallback(
    (receiver?: InvoiceFormReceiver) => {
      if (isEmpty(receiver)) {
        return
      }

      const { currencyId, eInvoiceScheme, ean, invoicingLanguage, paymentTermsDays, publicEntityRoutingEnabled } =
        receiver || {}

      setValue('receiver', receiver)
      handleLanguageUpdate(invoicingLanguage)
      handleCurrencyUpdate(currencyId)
      handlePaymentTermUpdate(paymentTermsDays)
      handleBuyersReferenceUpdate(eInvoiceScheme, ean, publicEntityRoutingEnabled)
    },
    [handleCurrencyUpdate, handleLanguageUpdate, handlePaymentTermUpdate, handleBuyersReferenceUpdate, setValue],
  )

  return { handleReceiverUpdate }
}
